import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import type { Transition } from '../../types/presentation.types'

/**
 * Plays the transition between two slides (MYD-496).
 *
 * Holds up to two layers: the slide arriving and, while a transition runs,
 * the slide leaving. Animations use the Web Animations API on `transform` and
 * `opacity` only, the two properties browsers composite on the GPU, which is
 * what keeps a page turn at 60fps on an ordinary laptop. No dependency.
 *
 *   flip   a 3D page turn about the slide's LEFT edge. Forward, the leaving
 *          page turns away to reveal the next beneath it; backward, the
 *          arriving page turns back over the one being left.
 *   slide  the new slide pushes the old one off, left or right.
 *   fade   a crossfade.
 *   none   an immediate cut.
 *
 * `reducedMotion` turns every transition into a short crossfade.
 *
 * A move that arrives mid-transition finishes the running one instantly first,
 * so fast clicking never leaves a half-turned page on screen.
 */

export const TIMINGS: Record<Transition, number> = { flip: 600, slide: 450, fade: 350, none: 0 }
const REDUCED_MOTION_MS = 200
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

interface Layer {
	key: string
	node: ReactNode
}

interface PageFlipProps {
	/** Identifies the slide shown. A change of key is what triggers a transition. */
	slideKey: string
	children: ReactNode
	transition: Transition
	direction: 1 | -1
	reducedMotion: boolean
}

type Keyframes = { arriving?: Keyframe[]; leaving?: Keyframe[]; arrivingOnTop: boolean; duration: number }

const keyframesFor = (transition: Transition, direction: 1 | -1, reducedMotion: boolean): Keyframes | null => {
	if (reducedMotion && transition !== 'none') {
		return { arriving: [{ opacity: 0 }, { opacity: 1 }], arrivingOnTop: true, duration: REDUCED_MOTION_MS }
	}
	switch (transition) {
		case 'flip':
			return direction === 1
				? {
						leaving: [
							{ transform: 'rotateY(0deg)' },
							{ transform: 'rotateY(-180deg)' },
						],
						arrivingOnTop: false,
						duration: TIMINGS.flip,
					}
				: {
						arriving: [
							{ transform: 'rotateY(-180deg)' },
							{ transform: 'rotateY(0deg)' },
						],
						arrivingOnTop: true,
						duration: TIMINGS.flip,
					}
		case 'slide':
			return {
				arriving: [{ transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }],
				leaving: [{ transform: 'translateX(0)' }, { transform: `translateX(${direction * -100}%)` }],
				arrivingOnTop: true,
				duration: TIMINGS.slide,
			}
		case 'fade':
			return { arriving: [{ opacity: 0 }, { opacity: 1 }], arrivingOnTop: true, duration: TIMINGS.fade }
		default:
			return null
	}
}

export function PageFlip({ slideKey, children, transition, direction, reducedMotion }: PageFlipProps) {
	const [current, setCurrent] = useState<Layer>({ key: slideKey, node: children })
	const [leaving, setLeaving] = useState<Layer | null>(null)
	const [plan, setPlan] = useState<Keyframes | null>(null)

	const arrivingRef = useRef<HTMLDivElement>(null)
	const leavingRef = useRef<HTMLDivElement>(null)
	const running = useRef<Animation[]>([])

	// Same slide, new content (a block resolved): update in place, no motion.
	if (slideKey === current.key && children !== current.node) {
		setCurrent({ key: slideKey, node: children })
	}

	// A new slide: the one on screen becomes the leaving layer.
	if (slideKey !== current.key) {
		const next = keyframesFor(transition, direction, reducedMotion)
		setLeaving(next ? current : null)
		setPlan(next)
		setCurrent({ key: slideKey, node: children })
	}

	useLayoutEffect(() => {
		// Finish anything still running, so two quick moves never overlap.
		running.current.forEach(animation => animation.finish())
		running.current = []

		if (!plan || !leaving) return
		const canAnimate = typeof arrivingRef.current?.animate === 'function'
		if (!canAnimate) {
			setLeaving(null)
			return
		}

		const options: KeyframeAnimationOptions = { duration: plan.duration, easing: EASING, fill: 'both' }
		const animations: Animation[] = []
		if (plan.arriving && arrivingRef.current) animations.push(arrivingRef.current.animate(plan.arriving, options))
		if (plan.leaving && leavingRef.current) animations.push(leavingRef.current.animate(plan.leaving, options))
		running.current = animations

		let cancelled = false
		Promise.all(animations.map(animation => animation.finished.catch(() => undefined))).then(() => {
			if (cancelled) return
			// Drop the fill, so the arriving layer is left with no transform.
			animations.forEach(animation => animation.cancel())
			setLeaving(null)
		})
		return () => {
			cancelled = true
		}
	}, [plan, leaving])

	const layerClass = 'absolute inset-0 [backface-visibility:hidden] [transform-origin:left_center] will-change-transform'
	const arrivingOnTop = plan?.arrivingOnTop ?? true

	return (
		<div className="relative h-full w-full [perspective:4000px]" data-slide-key={current.key}>
			{leaving && (
				<div ref={leavingRef} key={`leaving-${leaving.key}`} className={layerClass} style={{ zIndex: arrivingOnTop ? 1 : 2 }} aria-hidden>
					{leaving.node}
				</div>
			)}
			<div ref={arrivingRef} key={`current-${current.key}`} className={layerClass} style={{ zIndex: arrivingOnTop ? 2 : 1 }}>
				{current.node}
			</div>
		</div>
	)
}

export default PageFlip
